import { config } from '../config';

interface NavigationOptions {
  primaryNav: string;
  primaryNavList: string;
  navLinks: string;
  mobileNavIcon: string;
  mobileNavIconClose: string;
  navButtons: string;
  subNavs: string;
  activeSubButton: HTMLElement | null;
}

interface MegaMenuGrandchild {
  name: string;
  url: string;
  target: string;
  tourCount: number | null;
}

interface MegaMenuCardLink {
  name: string;
  url: string;
  target: string;
}

interface MegaMenuResponsiveImage {
  lg: string;
  md: string;
  sm: string;
  altText: string;
}

interface MegaMenuFeaturedTour {
  name: string;
  url: string;
  days: number;
  fromPrice: string;
  image: MegaMenuResponsiveImage | null;
}

interface MegaMenuChild {
  name: string;
  tourCount: number | null;
  cardLink: MegaMenuCardLink | null;
  cardImage: MegaMenuResponsiveImage | null;
  grandchildLinks: MegaMenuGrandchild[];
  partnerClass: string;
  partnershipColor: string | null;
  featuredTour: MegaMenuFeaturedTour | null;
}

interface MegaMenuPanel {
  childLinks: MegaMenuChild[];
}

let megaMenuDataPromise: Promise<(MegaMenuPanel | null)[]> | null = null;
const megaMenuLoadPromises = new WeakMap<HTMLElement, Promise<void>>();

function fetchMegaMenuData(): Promise<(MegaMenuPanel | null)[]> {
  const nav = document.querySelector<HTMLElement>('#primary-nav');
  const apiUrl = nav?.dataset.megamenuApi?.trim();
  if (!apiUrl) {
    return Promise.resolve([]);
  }
  if (!megaMenuDataPromise) {
    megaMenuDataPromise = fetch(apiUrl).then(r => {
      if (!r.ok) throw new Error(`Mega-menu API error: ${r.status}`);
      return r.json() as Promise<(MegaMenuPanel | null)[]>;
    }).catch(err => {
      megaMenuDataPromise = null;
      throw err;
    });
  }
  return megaMenuDataPromise;
}

function escHtml(str: string | null | undefined): string {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function tourCountText(count: number | null): string {
  if (!count) return '';
  return `${count} ${count === 1 ? 'tour' : 'tours'}`;
}

function pictureHtml(img: MegaMenuResponsiveImage | null, className: string): string {
  if (!img) return '';
  return `
    <picture>
      <source srcset="${escHtml(img.lg)}" media="(min-width: 992px)">
      <source srcset="${escHtml(img.md)}" media="(min-width: 768px)">
      <img src="${escHtml(img.sm)}" alt="${escHtml(img.altText)}" class="${className}" loading="lazy" decoding="async">
    </picture>`;
}

function drillInId(menuIndex: number, itemIndex: number): string {
  return `mega-menu-drill-${menuIndex}-${itemIndex}`;
}

function partnerAttributes(level2: MegaMenuChild): { partnerClass: string; partnershipStyle: string } {
  return {
    partnerClass: level2.partnerClass ? ` ${escHtml(level2.partnerClass)}` : '',
    partnershipStyle: level2.partnershipColor ? ` style="--partnership-color:${escHtml(level2.partnershipColor)}"` : ''
  };
}

function renderCard(level2: MegaMenuChild, menuIndex: number, itemIndex: number): string {
  const { partnerClass, partnershipStyle } = partnerAttributes(level2);
  const count = tourCountText(level2.tourCount);

  return `
    <li class="mega-menu-grid__item">
      <button type="button" class="mega-menu-card${partnerClass}"${partnershipStyle} aria-expanded="false" aria-controls="${drillInId(menuIndex, itemIndex)}">
        ${pictureHtml(level2.cardImage, 'mega-menu-card__img')}
        <span class="mega-menu-card__body">
          <span class="mega-menu-card__title">${escHtml(level2.name)}</span>
          ${count ? `<span class="mega-menu-card__count">${count}</span>` : ''}
        </span>
      </button>
    </li>`;
}

function renderFeaturedTour(tour: MegaMenuFeaturedTour): string {
  const meta = [tour.days ? `${tour.days} days` : '', tour.fromPrice ? `from ${escHtml(tour.fromPrice)}pp` : ''].filter(Boolean).join(' &middot; ');
  return `
    <a href="${escHtml(tour.url)}" class="mega-menu-drill__media mega-menu-tour">
      ${pictureHtml(tour.image, 'mega-menu-tour__img')}
      <span class="mega-menu-tour__eyebrow">Featured tour</span>
      <span class="mega-menu-tour__name">${escHtml(tour.name)}</span>
      <span class="mega-menu-tour__meta"><span>${meta}</span><span aria-hidden="true">&rarr;</span></span>
    </a>`;
}

function tourSearchUrl(searchUrl: string, continent: string | null, country: string | null): string {
  const params = new URLSearchParams();
  if (continent) params.set('Continents[0]', continent);
  if (country) params.set('Countries[0]', country);
  return `${searchUrl}?${params.toString()}`;
}

function renderDrillIn(level2: MegaMenuChild, menuIndex: number, itemIndex: number, menuTitle: string, isOnlyItem: boolean, searchUrl: string): string {
  const { partnerClass, partnershipStyle } = partnerAttributes(level2);
  const count = tourCountText(level2.tourCount);
  const continent = searchUrl && level2.tourCount ? level2.name : null;

  const linksHtml = level2.grandchildLinks
    .filter(level3 => level3.url !== level2.cardLink?.url)
    .map(level3 => {
      const linkCount = level3.tourCount ? `<span class="mega-menu-drill__link-count">${level3.tourCount}</span>` : '';
      const link = searchUrl && level3.tourCount
        ? `href="${escHtml(tourSearchUrl(searchUrl, null, level3.name))}"`
        : `href="${escHtml(level3.url)}" target="${escHtml(level3.target)}"`;
      return `<li class="mega-menu-drill__item"><a ${link} class="mega-menu-drill__link"><span>${escHtml(level3.name)}</span>${linkCount}</a></li>`;
    })
    .join('');

  const ctaLink = continent
    ? `href="${escHtml(tourSearchUrl(searchUrl, continent, null))}"`
    : `href="${escHtml(level2.cardLink?.url ?? '')}" target="${escHtml(level2.cardLink?.target ?? '')}"`;
  const ctaHtml = level2.cardLink?.url
    ? `<a ${ctaLink} class="btn mega-menu-drill__cta">${escHtml(level2.cardLink.name || level2.name)}</a>`
    : '';
  let mediaHtml = '';
  if (level2.featuredTour) {
    mediaHtml = renderFeaturedTour(level2.featuredTour);
  } else if (level2.cardImage) {
    mediaHtml = `<div class="mega-menu-drill__media">${pictureHtml(level2.cardImage, 'mega-menu-drill__img')}</div>`;
  }

  return `
    <div class="mega-menu-drill${partnerClass}" id="${drillInId(menuIndex, itemIndex)}"${partnershipStyle}${isOnlyItem ? '' : ' hidden'}>
      ${isOnlyItem ? '' : `<button type="button" class="mega-menu-drill__back"><svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M20 12H4M10 6l-6 6 6 6" stroke="currentColor" stroke-width="2.5" fill="none"/></svg>All ${escHtml(menuTitle)}</button>`}
      <div class="mega-menu-drill__layout">
        ${mediaHtml}
        <div class="mega-menu-drill__content">
          <h3 class="mega-menu-drill__title" tabindex="-1">${escHtml(level2.name)}${count ? ` <span class="mega-menu-drill__count">${count}</span>` : ''}</h3>
          ${linksHtml ? `<ul class="mega-menu-drill__links">${linksHtml}</ul>` : ''}
          ${ctaHtml ? `<div class="mega-menu-drill__actions">${ctaHtml}</div>` : ''}
        </div>
      </div>
    </div>`;
}

function renderDesktopPanel(childLinks: MegaMenuChild[], menuIndex: number, menuTitle: string, searchUrl: string): string {
  const isOnlyItem = childLinks.length === 1;
  const gridHtml = isOnlyItem
    ? ''
    : `
      <div class="mega-menu-grid">
        <p class="mega-menu-grid__eyebrow">${escHtml(menuTitle)}</p>
        <ul class="mega-menu-grid__list">${childLinks.map((level2, i) => renderCard(level2, menuIndex, i)).join('')}</ul>
      </div>`;
  const drillInsHtml = childLinks.map((level2, i) => renderDrillIn(level2, menuIndex, i, menuTitle, isOnlyItem, searchUrl)).join('');

  const closeHtml = `<button type="button" class="mega-menu-desktop__close">Close <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2.5" fill="none"/></svg></button>`;

  return `<div class="mega-menu-desktop container">${closeHtml}${gridHtml}${drillInsHtml}</div>`;
}

function showMegaMenuGrid(megaMenu: HTMLElement): void {
  megaMenu.querySelectorAll<HTMLElement>('.mega-menu-card').forEach(card => card.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false'));
  const grid = megaMenu.querySelector<HTMLElement>('.mega-menu-grid');
  if (!grid) return;
  grid.hidden = false;
  megaMenu.querySelectorAll<HTMLElement>('.mega-menu-drill').forEach(drillIn => { drillIn.hidden = true; });
}

const EASE_OUT = 'cubic-bezier(0.2, 0, 0, 1)';
const EASE_IN = 'cubic-bezier(0.4, 0, 1, 1)';
const HEIGHT_ANIMATION_ID = 'mega-menu-height';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let visibleMegaMenu: HTMLElement | null = null;

function animate(el: Element | null | undefined, keyframes: Keyframe[], options: KeyframeAnimationOptions): Animation | null {
  if (!el || reducedMotion.matches) return null;
  return el.animate(keyframes, options);
}

function panelHeight(megaMenu: HTMLElement): number {
  return megaMenu.querySelector<HTMLElement>('.mega-menu__inner')?.offsetHeight ?? 0;
}

function revealContent(content: Element | null | undefined): void {
  animate(content, [{ opacity: 0, transform: 'translateY(0.8rem)' }, { opacity: 1, transform: 'none' }], { duration: 300, easing: EASE_OUT });
}

function morphPanelHeight(megaMenu: HTMLElement, fromHeight: number): void {
  const inner = megaMenu.querySelector<HTMLElement>('.mega-menu__inner');
  if (!inner) return;
  inner.getAnimations().forEach(animation => {
    if (animation.id === HEIGHT_ANIMATION_ID) animation.cancel();
  });
  const toHeight = inner.offsetHeight;
  if (toHeight === fromHeight) return;
  animate(inner, [
    { height: `${fromHeight}px`, overflow: 'hidden' },
    { height: `${toHeight}px`, overflow: 'hidden' }
  ], { id: HEIGHT_ANIMATION_ID, duration: 300, easing: EASE_OUT });
}

function hideMegaMenu(megaMenu: HTMLElement): void {
  megaMenu.getAnimations().forEach(animation => animation.cancel());
  megaMenu.style.display = 'none';
  showMegaMenuGrid(megaMenu);
  if (visibleMegaMenu === megaMenu) {
    visibleMegaMenu = null;
  }
}

function showMegaMenu(megaMenu: HTMLElement): void {
  const previous = visibleMegaMenu;
  const previousHeight = previous ? panelHeight(previous) : 0;
  if (previous && previous !== megaMenu) {
    hideMegaMenu(previous);
  }

  megaMenu.getAnimations().forEach(animation => animation.cancel());
  megaMenu.style.display = 'block';
  visibleMegaMenu = megaMenu;
  if (previous === megaMenu) return;

  const content = megaMenu.querySelector('.mega-menu-desktop');
  if (previous) {
    morphPanelHeight(megaMenu, previousHeight);
  } else {
    animate(megaMenu, [{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    animate(megaMenu.querySelector('.mega-menu__inner'), [{ transform: 'translateY(-1.2rem)' }, { transform: 'none' }], { duration: 300, easing: EASE_OUT });
  }
  revealContent(content);
}

function bindDesktopPanel(megaMenu: HTMLElement): void {
  megaMenu.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement;

    // The backdrop is the panel's :after, so its clicks land on the panel element itself.
    if (target === megaMenu) {
      closeDesktopNavItem();
      return;
    }

    if (target.closest('.mega-menu-desktop__close')) {
      const navButton = OPTIONS.activeSubButton;
      closeDesktopNavItem();
      navButton?.focus();
      return;
    }

    const card = target.closest<HTMLElement>('.mega-menu-card');
    if (card) {
      const drillIn = document.getElementById(card.getAttribute(ATTRIBUTES.ARIA_CONTROLS) ?? '');
      const grid = megaMenu.querySelector<HTMLElement>('.mega-menu-grid');
      if (!drillIn || !grid) return;
      const fromHeight = panelHeight(megaMenu);
      grid.hidden = true;
      drillIn.hidden = false;
      morphPanelHeight(megaMenu, fromHeight);
      revealContent(drillIn);
      card.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');
      drillIn.querySelector<HTMLElement>('.mega-menu-drill__title')?.focus();
      return;
    }

    if (target.closest('.mega-menu-drill__back')) {
      const drillIn = target.closest<HTMLElement>('.mega-menu-drill');
      const originCard = drillIn ? megaMenu.querySelector<HTMLElement>(`.mega-menu-card[aria-controls="${drillIn.id}"]`) : null;
      const fromHeight = panelHeight(megaMenu);
      showMegaMenuGrid(megaMenu);
      morphPanelHeight(megaMenu, fromHeight);
      revealContent(megaMenu.querySelector('.mega-menu-grid'));
      originCard?.focus();
    }
  });
}

function grandchildItemsHtml(links: MegaMenuGrandchild[]): string {
  return links
    .map(level3 =>
      `<li class="nav-level-3__item"><a href="${escHtml(level3.url)}" target="${escHtml(level3.target)}" aria-label="${escHtml(level3.name)}" class="nav-level-3__link">${escHtml(level3.name)}</a></li>`
    )
    .join('');
}

function fillMobileGrandchildren(megaMenu: HTMLElement, childLinks: MegaMenuChild[]): void {
  if (!childLinks.length) return;

  const hideLevel2 = childLinks.length === 1;
  if (hideLevel2) {
    const ul = megaMenu.querySelector<HTMLUListElement>(':scope .nav-level-2-wrapper > .nav-level-3');
    if (ul && ul.children.length === 0) {
      ul.innerHTML = grandchildItemsHtml(childLinks[0].grandchildLinks);
    }
    return;
  }

  const items = megaMenu.querySelectorAll<HTMLElement>('.nav-level-2 > li');
  items.forEach((item, i) => {
    const ul = item.querySelector<HTMLUListElement>('.nav-level-3');
    const links = childLinks[i]?.grandchildLinks ?? [];
    if (ul && ul.children.length === 0) {
      ul.innerHTML = grandchildItemsHtml(links);
    }
  });
}

function loadMegaMenuPanel(megaMenu: HTMLElement): Promise<void> {
  if (megaMenu.dataset.loaded === 'true') {
    return Promise.resolve();
  }

  const existing = megaMenuLoadPromises.get(megaMenu);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    megaMenu.dataset.loading = 'true';
    megaMenu.setAttribute('aria-busy', 'true');

    try {
      const allPanels = await fetchMegaMenuData();
      const menuIndex = parseInt(megaMenu.dataset.menuIndex ?? '0', 10);
      const panelData = allPanels[menuIndex];

      if (panelData) {
        const inner = megaMenu.querySelector<HTMLElement>('.mega-menu__inner');
        if (inner && !inner.querySelector('.mega-menu-desktop')) {
          const searchUrl = menuIndex === 0 ? document.querySelector<HTMLElement>('#primary-nav')?.dataset.searchUrl ?? '' : '';
          const emptyHeight = inner.offsetHeight;
          inner.insertAdjacentHTML('beforeend', renderDesktopPanel(panelData.childLinks, menuIndex, megaMenu.dataset.menuTitle ?? '', searchUrl));
          bindDesktopPanel(megaMenu);
          if (megaMenu === visibleMegaMenu) {
            morphPanelHeight(megaMenu, emptyHeight);
            revealContent(inner.querySelector('.mega-menu-desktop'));
          }
        }
        fillMobileGrandchildren(megaMenu, panelData.childLinks);
      }

      megaMenu.dataset.loaded = 'true';
      megaMenu.removeAttribute('data-loading');
      megaMenu.removeAttribute('aria-busy');
    } catch {
      megaMenu.removeAttribute('data-loading');
      megaMenu.removeAttribute('aria-busy');
    } finally {
      megaMenuLoadPromises.delete(megaMenu);
    }
  })();

  megaMenuLoadPromises.set(megaMenu, promise);
  return promise;
}

const OPTIONS: NavigationOptions = {
  primaryNav: '#primary-nav',
  primaryNavList: '#primary-nav-list',
  navLinks: '#primary-nav-list li',
  mobileNavIcon: '.mobile-nav-icon',
  mobileNavIconClose: '.mobile-nav-icon-close',
  navButtons: '.nav-button',
  subNavs: '.nav-level-2',
  activeSubButton: null
};

const KEY_CODES = {
  ENTER: 13,
  SPACE: 32,
  ESCAPE: 27,
  TAB: 9
} as const;

const CLASSES = {
  ACTIVE: 'is-active',
  NAV_ACTIVE: 'nav-active',
  MOBILE_NAV_ALT: 'mobile-nav-icon-close',
  MEGA_ACTIVE: 'mega-active',
  HIDDEN: 'hidden',
  JS_LOADED: 'js-loaded'
} as const;

const ATTRIBUTES = {
  ARIA_EXPANDED: 'aria-expanded',
  ARIA_HIDDEN: 'aria-hidden',
  ARIA_CONTROLS: 'aria-controls',
  ARIA_LABELLEDBY: 'aria-labelledby',
  TABINDEX: 'tabindex',
  ID: 'ID'
} as const;

let mobileNavListClickHandler: ((e: Event) => void) | null = null;
let mobileIconClickHandler: ((e: Event) => void) | null = null;
let desktopMegaCloseHandler: ((e: Event) => void) | null = null;
let desktopNavMouseDownHandler: ((e: MouseEvent) => void) | null = null;
let desktopNavKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
let desktopOutsideClickHandler: ((e: Event) => void) | null = null;
let desktopFocusInHandler: ((e: FocusEvent) => void) | null = null;

function teardownDesktopDelegatedListeners(): void {
  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

  if (desktopMegaCloseHandler && primaryNav) {
    primaryNav.removeEventListener('click', desktopMegaCloseHandler);
  }
  desktopMegaCloseHandler = null;

  if (desktopNavMouseDownHandler && primaryNav) {
    primaryNav.removeEventListener('mousedown', desktopNavMouseDownHandler);
  }
  desktopNavMouseDownHandler = null;

  if (desktopNavKeydownHandler && primaryNav) {
    primaryNav.removeEventListener('keydown', desktopNavKeydownHandler);
  }
  desktopNavKeydownHandler = null;

  if (desktopOutsideClickHandler) {
    document.removeEventListener('click', desktopOutsideClickHandler);
  }
  desktopOutsideClickHandler = null;

  if (desktopFocusInHandler) {
    document.removeEventListener('focusin', desktopFocusInHandler);
  }
  desktopFocusInHandler = null;
}

function toggleMobileSubmenu(button: HTMLElement, submenu: HTMLElement): void {
  const isExpanded = button.getAttribute(ATTRIBUTES.ARIA_EXPANDED) === 'true';
  const newState = !isExpanded;

  button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, newState.toString());
  button.classList.toggle(CLASSES.ACTIVE);
  submenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, (!newState).toString());

  if (newState) {
    submenu.style.display = 'block';
    submenu.style.height = '0px';
    submenu.style.overflow = 'hidden';
    submenu.style.transition = 'height 0.3s ease-out';

    const naturalHeight = submenu.scrollHeight;

    requestAnimationFrame(() => {
      submenu.style.height = naturalHeight + 'px';
    });

    setTimeout(() => {
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
    }, 300);
  } else {
    submenu.style.display = 'block';
    submenu.style.height = 'auto';
    submenu.style.overflow = 'hidden';

    const currentHeight = submenu.offsetHeight;

    submenu.style.height = currentHeight + 'px';
    submenu.style.transition = 'height 0.3s ease-in';

    submenu.offsetHeight;

    requestAnimationFrame(() => {
      submenu.style.height = '0px';
    });

    setTimeout(() => {
      submenu.style.display = 'none';
      submenu.style.height = '';
      submenu.style.overflow = '';
      submenu.style.transition = '';
    }, 300);
  }
}

function setHeaderHeight(measuredHeight?: number): void {
  const header = document.querySelector<HTMLElement>('#header, .header');
  if (header) {
    // offsetHeight forces a synchronous layout; prefer the size ResizeObserver already measured.
    const headerHeight = measuredHeight ?? header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  }
}

export function init(): void {
  const mqlMobile = window.matchMedia(`(max-width: ${config.breakpoint.mdMax}px)`);
  const mqlDesktop = window.matchMedia(`(min-width: ${config.breakpoint.lgMin}px)`);

  const handleMobileNav = (e: MediaQueryListEvent | MediaQueryList): void => {
    if (e.matches) {
      requestAnimationFrame(() => {
        // The ResizeObserver below reports the height on observe and on every change.
        if (typeof ResizeObserver === 'undefined') {
          setHeaderHeight();
        }
        createMobileMenu();
      });
    } else {
      destroyMobileMenu();
    }
  };

  const handleDesktopNav = (e: MediaQueryListEvent | MediaQueryList): void => {
    if (e.matches) {
      requestAnimationFrame(() => {
        createDesktopMenu();
      });
    } else {
      destroyDesktopMenu();
    }
  };

  mqlMobile.addEventListener('change', handleMobileNav);
  mqlDesktop.addEventListener('change', handleDesktopNav);
  
  handleMobileNav(mqlMobile);
  handleDesktopNav(mqlDesktop);

  createNavigation();

  const header = document.querySelector<HTMLElement>('#header, .header');
  if (header && typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver((entries) => {
      const blockSize = entries[0]?.borderBoxSize?.[0]?.blockSize;
      const measuredHeight = blockSize === undefined ? undefined : Math.round(blockSize);
      requestAnimationFrame(() => {
        setHeaderHeight(measuredHeight);
      });
    });
    resizeObserver.observe(header);
  }
}

function createMobileMenu(): void {
  const megaMenus = document.querySelectorAll<HTMLElement>('.mega-menu');
  megaMenus.forEach(menu => menu.removeAttribute('style'));

  const navLinks = document.querySelectorAll<HTMLElement>(OPTIONS.navLinks);
  navLinks.forEach((link, index) => {
    const submenu = link.querySelector('ul');
    if (!submenu) return;

    const buttonId = `mobile-nav-heading-${index}`;
    const contentId = `mobile-nav-accordion-${index}`;

    const heading = link.querySelector<HTMLElement>('.mobile-heading:first-child');
    if (!heading) return;

    const button = document.createElement('button');
    button.type = 'button';
    button.id = buttonId;
    button.className = 'nav-button';
    button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
    button.setAttribute(ATTRIBUTES.ARIA_CONTROLS, contentId);
    button.innerHTML = `${heading.innerHTML}<span role="presentation" class="nav-button__icon"></span>`;

    heading.innerHTML = '';
    heading.appendChild(button);

    submenu.id = contentId;
    submenu.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, buttonId);
    submenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
  });

  const navList = document.querySelector<HTMLElement>(OPTIONS.primaryNavList);
  if (mobileNavListClickHandler && navList) {
    navList.removeEventListener('click', mobileNavListClickHandler);
  }
  mobileNavListClickHandler = (e: Event) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.nav-button');
    if (!btn || !navList?.contains(btn)) return;
    const contentId = btn.getAttribute(ATTRIBUTES.ARIA_CONTROLS);
    const submenu = contentId ? document.getElementById(contentId) : null;
    if (!(submenu instanceof HTMLElement)) return;

    const megaMenu = btn.closest<HTMLElement>('.mega-menu')
      ?? btn.closest('.nav-level-1__item')?.querySelector<HTMLElement>('.mega-menu');

    if (megaMenu && megaMenu.dataset.loaded !== 'true') {
      e.preventDefault();
      if (megaMenu.dataset.loading === 'true') return;
      loadMegaMenuPanel(megaMenu).then(() => {
        toggleMobileSubmenu(btn, submenu);
      });
      return;
    }

    toggleMobileSubmenu(btn, submenu);
  };
  navList?.addEventListener('click', mobileNavListClickHandler);

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
  if (primaryNav) {
    primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');

    // Setup open/close icons
    const openIcon = document.querySelector<HTMLElement>(OPTIONS.mobileNavIcon);
    const closeIcon = document.querySelector<HTMLElement>(OPTIONS.mobileNavIconClose);
   
    if (openIcon) {
      openIcon.setAttribute(ATTRIBUTES.ARIA_CONTROLS, primaryNav.id || 'primary-nav');
      openIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      openIcon.classList.remove('d-none');
    }
    if (closeIcon) {
      closeIcon.setAttribute(ATTRIBUTES.ARIA_CONTROLS, primaryNav.id || 'primary-nav');
      closeIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      closeIcon.classList.add('d-none');
    }

    // Remove previous listeners
    if (mobileIconClickHandler) {
      document.removeEventListener('click', mobileIconClickHandler);
    }

    // Handler for open icon
    const handleOpen = (e: Event) => {
      const icon = (e.target as HTMLElement).closest<HTMLElement>(OPTIONS.mobileNavIcon);
      if (!icon) return;
      e.preventDefault();
     
      primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
      document.documentElement.classList.add(CLASSES.NAV_ACTIVE);
      icon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');
      // Toggle visibility
      if (openIcon) {
        openIcon.classList.add('d-none');
      }

      if (closeIcon) {
        closeIcon.classList.remove('d-none');
      } 
     
      // Hide usp bars
      const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
      uspBars.forEach(uspBar => uspBar.classList.add('d-none'));

      fetchMegaMenuData().catch(() => { /* warmup; first accordion retries on failure */ });
    };

    // Handler for close icon
    const handleClose = (e: Event) => {
      const icon = (e.target as HTMLElement).closest<HTMLElement>(OPTIONS.mobileNavIconClose);
      if (!icon) return;
      e.preventDefault();
      
      primaryNav.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
      document.documentElement.classList.remove(CLASSES.NAV_ACTIVE);
      // Toggle visibility
      if (openIcon) {
        openIcon.classList.remove('d-none');
      }

      if (closeIcon) {
        closeIcon.classList.add('d-none');
      }

      if (openIcon) {
        openIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      }
      
      if (closeIcon) {
        closeIcon.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
      }
     
      // Show usp bars
      const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
      uspBars.forEach(uspBar => uspBar.classList.remove('d-none'));
    };

    // Attach listeners
    if (openIcon) {
      openIcon.addEventListener('click', handleOpen);
    }
    
    if (closeIcon) {
      closeIcon.addEventListener('click', handleClose);
    }
  }
}

function destroyMobileMenu(): void {
  const navList = document.querySelector<HTMLElement>(OPTIONS.primaryNavList);
  if (mobileNavListClickHandler && navList) {
    navList.removeEventListener('click', mobileNavListClickHandler);
  }
  mobileNavListClickHandler = null;

  if (mobileIconClickHandler) {
    document.removeEventListener('click', mobileIconClickHandler);
  }
  mobileIconClickHandler = null;

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
  if (primaryNav) {
    primaryNav.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
  }

  const navButtons = document.querySelectorAll<HTMLElement>(OPTIONS.navButtons);
  navButtons.forEach(button => {
    const handler = (button as any).__clickHandler;
    if (handler) {
      button.removeEventListener('click', handler);
    }
  });

  const focusElements = document.querySelectorAll<HTMLElement>('.nav-button, .nav-level-1__link, .secondary-nav__link');
  focusElements.forEach(element => {
    const handler = (element as any).__focusHandler;
    if (handler) {
      element.removeEventListener('focus', handler);
    }
  });

  document.documentElement.classList.remove(CLASSES.NAV_ACTIVE);
  
  const uspBars = document.querySelectorAll<HTMLElement>('.usp-bar, .usp-barTs');
  uspBars.forEach(uspBar => {
    uspBar.style.visibility = 'visible';
  });

  const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
  activeButtons.forEach(button => {
    button.click();
  });

  const links = document.querySelectorAll<HTMLElement>('a:not(#primary-nav a)');
  links.forEach(link => {
    link.setAttribute(ATTRIBUTES.TABINDEX, '0');
    link.style.cursor = 'pointer';
  });

  OPTIONS.activeSubButton = null;

  const navItems = document.querySelectorAll<HTMLElement>('.nav-level-1__item');
  navItems.forEach(item => {
    const button = item.querySelector('button');
    if (button) {
      const parent = button.parentElement;
      if (parent) {
        while (button.firstChild) {
          parent.insertBefore(button.firstChild, button);
        }
        parent.removeChild(button);
      }
    }
    
    const icons = item.querySelectorAll<HTMLElement>('.nav-button__icon');
    icons.forEach(icon => icon.remove());
  });

  const navLevels = document.querySelectorAll<HTMLElement>('.nav-level-2, .nav-level-3');
  navLevels.forEach(level => {
    level.removeAttribute(ATTRIBUTES.ARIA_LABELLEDBY);
    level.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
    level.removeAttribute(ATTRIBUTES.ID);
    level.removeAttribute('style');
  });
}

function createDesktopMenu(): void {
  teardownDesktopDelegatedListeners();

  const primaryNav = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

  const navLinks = document.querySelectorAll<HTMLElement>(OPTIONS.navLinks);
  navLinks.forEach((link, index) => {
    const megaMenu = link.querySelector<HTMLElement>('.mega-menu');
    if (!megaMenu) return;

    const buttonId = `desktop-nav-heading-${index}`;
    const contentId = `desktop-nav-accordion-${index}`;

    const heading = link.querySelector<HTMLElement>('.nav-level-1__heading');
    if (!heading) return;

    const buttonText = heading.innerHTML;
    
    const button = document.createElement('button');
    button.type = 'button';
    button.id = buttonId;
    button.className = 'nav-button';
    button.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
    button.setAttribute(ATTRIBUTES.ARIA_CONTROLS, contentId);
    button.innerHTML = `${buttonText}<span role="presentation" class="nav-button__icon"></span>`;

    heading.classList.add(CLASSES.JS_LOADED);
    heading.innerHTML = '';
    heading.appendChild(button);

    button.addEventListener('mouseenter', () => {
      loadMegaMenuPanel(megaMenu).catch(() => { /* silently ignore prefetch errors */ });
    }, { passive: true });

    megaMenu.id = contentId;
    megaMenu.setAttribute(ATTRIBUTES.ARIA_LABELLEDBY, buttonId);
    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
  });

  if (primaryNav) {
    desktopMegaCloseHandler = (e: Event) => {
      if (!(e.target as HTMLElement).closest?.('.js-mega-close')) return;
      closeDesktopNavItem();
    };
    primaryNav.addEventListener('click', desktopMegaCloseHandler);

    desktopNavMouseDownHandler = (event: MouseEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.nav-button');
      if (!target || !primaryNav.contains(target)) return;
      if (target.classList.contains(CLASSES.ACTIVE)) {
        closeDesktopNavItem();
      } else {
        openDesktopNavItem(target, false);
      }
    };
    primaryNav.addEventListener('mousedown', desktopNavMouseDownHandler);

    desktopNavKeydownHandler = (event: KeyboardEvent) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('.nav-button');
      if (!target || !primaryNav.contains(target)) return;
      if (event.which === KEY_CODES.ENTER || event.which === KEY_CODES.SPACE) {
        event.preventDefault();
        if (target.classList.contains(CLASSES.ACTIVE)) {
          closeDesktopNavItem();
        } else {
          openDesktopNavItem(target, true);
        }
      }
    };
    primaryNav.addEventListener('keydown', desktopNavKeydownHandler);
  }

  desktopOutsideClickHandler = (event: Event) => {
    const primaryNavEl = document.querySelector<HTMLElement>(OPTIONS.primaryNav);
    if (primaryNavEl && !primaryNavEl.contains(event.target as Node)) {
      closeDesktopNavItem();
    }
  };
  document.addEventListener('click', desktopOutsideClickHandler);

  desktopFocusInHandler = (event: FocusEvent) => {
    if (!OPTIONS.activeSubButton) return;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const navRoot = document.querySelector<HTMLElement>(OPTIONS.primaryNav);

    if (target.matches('.nav-button') || target.closest('.nav-button')) {
      closeDesktopNavItem();
      return;
    }
    if (target.matches('.nav-level-1__link') || target.closest('.nav-level-1__link')) {
      closeDesktopNavItem();
      return;
    }
    if (target.matches('.secondary-nav__link') || target.closest('.secondary-nav__link')) {
      closeDesktopNavItem();
      return;
    }
    const interactive = target.closest('a, button, input');
    if (interactive && navRoot && !navRoot.contains(interactive)) {
      closeDesktopNavItem();
    }
  };
  document.addEventListener('focusin', desktopFocusInHandler);

  createStickyNav();

  // Warm up: prefetch all mega-menu data after the page is idle so the
  // first hover is instant without competing with LCP/FID resources.
  const scheduleWarmUp = (): void => {
    const nav = document.querySelector<HTMLElement>('#primary-nav');
    if (!nav?.dataset.megamenuApi?.trim()) return;
    fetchMegaMenuData().catch(() => { /* silently ignore prefetch errors */ });
  };

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(scheduleWarmUp, { timeout: 3000 });
  } else {
    setTimeout(scheduleWarmUp, 2000);
  }
}

const preventPageScroll = (event: Event): void => {
  const panel = (event.target as Element).closest?.('.mega-menu-desktop');
  if (panel && panel.scrollHeight > panel.clientHeight) return;
  event.preventDefault();
};

function openDesktopNavItem(element: HTMLElement, fromKeyboard: boolean): void {
  if (OPTIONS.activeSubButton) {
    closeDesktopNavItem();
  }

  const megaMenu = element.closest('li')?.querySelector<HTMLElement>('.mega-menu');
  if (!megaMenu) return;

  requestAnimationFrame(() => {
    OPTIONS.activeSubButton = element;

    element.classList.add(CLASSES.ACTIVE);
    element.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'true');

    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'false');
    megaMenu.classList.add(CLASSES.MEGA_ACTIVE);
    window.addEventListener('wheel', preventPageScroll, { passive: false });
    window.addEventListener('touchmove', preventPageScroll, { passive: false });
    showMegaMenu(megaMenu);

    const focusFirst = (): void => {
      if (!fromKeyboard) return;
      megaMenu.querySelector<HTMLElement>('.mega-menu-card, .mega-menu-drill__title')?.focus();
    };

    if (megaMenu.dataset.loaded !== 'true') {
      loadMegaMenuPanel(megaMenu).then(() => {
        if (OPTIONS.activeSubButton === element) {
          focusFirst();
        }
      });
    } else {
      focusFirst();
    }
  });
}

function closeDesktopNavItem(): void {
  if (!OPTIONS.activeSubButton) return;

  const element = OPTIONS.activeSubButton;
  OPTIONS.activeSubButton = null;
  
  element.classList.remove(CLASSES.ACTIVE);
  element.setAttribute(ATTRIBUTES.ARIA_EXPANDED, 'false');
  window.removeEventListener('wheel', preventPageScroll);
  window.removeEventListener('touchmove', preventPageScroll);

  const megaMenu = element.closest('li')?.querySelector<HTMLElement>('.mega-menu');
  if (megaMenu) {
    megaMenu.setAttribute(ATTRIBUTES.ARIA_HIDDEN, 'true');
    megaMenu.classList.remove(CLASSES.MEGA_ACTIVE);

    const fadeOut = animate(megaMenu, [{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: EASE_IN, fill: 'forwards' });
    if (fadeOut) {
      fadeOut.onfinish = () => hideMegaMenu(megaMenu);
    } else {
      hideMegaMenu(megaMenu);
    }
  }

  element.focus();
}

function destroyDesktopMenu(): void {
  teardownDesktopDelegatedListeners();
  window.removeEventListener('wheel', preventPageScroll);
  window.removeEventListener('touchmove', preventPageScroll);

  const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
  activeButtons.forEach(button => button.click());

  const navItems = document.querySelectorAll<HTMLElement>('.nav-level-1__item');
  navItems.forEach(item => {
    const button = item.querySelector('button');
    if (button) {
      const parent = button.parentElement;
      if (parent) {
        while (button.firstChild) {
          parent.insertBefore(button.firstChild, button);
        }
        parent.removeChild(button);
      }
    }
    
    const icons = item.querySelectorAll<HTMLElement>('.nav-button__icon');
    icons.forEach(icon => icon.remove());
  });

  const megaMenus = document.querySelectorAll<HTMLElement>('.mega-menu');
  megaMenus.forEach(menu => {
    menu.removeAttribute(ATTRIBUTES.ARIA_LABELLEDBY);
    menu.removeAttribute(ATTRIBUTES.ARIA_HIDDEN);
    menu.removeAttribute(ATTRIBUTES.ID);
    menu.removeAttribute('style');
    menu.style.zIndex = '-1';
  });

  const navLevel2Items = document.querySelectorAll<HTMLElement>('.nav-level-2__item a');
  navLevel2Items.forEach(item => item.classList.remove(CLASSES.HIDDEN));

  const megaCloseButtons = document.querySelectorAll<HTMLElement>('.js-mega-close');
  megaCloseButtons.forEach(button => button.remove());
}

function createNavigation(): void {
  document.addEventListener('keydown', (event) => {
    if (event.which === KEY_CODES.ESCAPE) {
      if (document.documentElement.classList.contains(CLASSES.NAV_ACTIVE)) {
        const activeButtons = document.querySelectorAll<HTMLElement>('.nav-button.is-active');
        activeButtons.forEach(button => button.click());
        
        const mobileNavIcon = document.querySelector<HTMLElement>('#mobile-nav-icon-2');
        if (mobileNavIcon) {
          mobileNavIcon.click();
        }
      }

      if (document.documentElement.classList.contains('search-active')) {
        const mobileSearchIcon = document.querySelector<HTMLElement>('#mobile-search-icon-2');
        if (mobileSearchIcon) {
          mobileSearchIcon.click();
        }
      }

      closeDesktopNavItem();

      const countryButton = document.querySelector<HTMLElement>('.selected-country-button.is-active');
      if (countryButton) {
        countryButton.click();
      }
    }
  });
}

function createStickyNav(): void {
  const stickyTarget = document.querySelector<HTMLElement>('.js-sticky-target');
  if (!stickyTarget) return;

  const stickyNavTop = stickyTarget.getBoundingClientRect().top + window.pageYOffset;

  const stickyNav = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (scrollTop > stickyNavTop) {
      stickyTarget.classList.add('sticky-nav');

      closeDesktopNavItem();

      const countryButton = document.querySelector<HTMLElement>('.selected-country-button.is-active');
      if (countryButton) {
        countryButton.click();
      }
    } else {
      stickyTarget.classList.remove('sticky-nav');
    }
  };

  stickyNav();

  window.addEventListener('scroll', stickyNav, { passive: true });
}

export default { init };